import { Box, Link, Typography } from "@mui/material";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckIcon from '@mui/icons-material/Check'
import { Link as RouterLink } from "react-router-dom";
import Section, { Container } from "../shared/Section";
import SectionTitle from "../shared/SectionTitle";
import TvButton from "../shared/TvButton";
import ServiceCard from "../shared/ServiceCard";
import CaseStudyCard from "../shared/CaseStudyCard";
import ProjectCard from "../shared/ProjectCard";
import CtaBanner from "../shared/CtaBanner";
import TechStack from "../shared/TechStack";
import { COLORS, SITE } from "../../constants";
import { caseStudies, credentials, employers, painPoints, processSteps, services } from "../../utils/siteContent";
import usePageMeta from "../../utils/usePageMeta";
import resumeData from "../../utils/resumeData";

const clientSite = resumeData.projects.find((p) => p.kind === 'Client website')

function Hero() {
    return (
        <Box
            component="header"
            sx={{
                backgroundColor: COLORS.black,
                color: COLORS.white,
                pt: { xs: 7, md: 12 },
                pb: { xs: 8, md: 13 },
                // Subtle cyan glow; no animation
                backgroundImage: `radial-gradient(ellipse 60% 50% at 85% 20%, rgba(32,197,245,0.14), transparent 70%)`,
            }}
        >
            <Container>
                <Typography variant="overline" component="p" sx={{ color: COLORS.accent, mb: 2 }}>
                    Freelance software engineer · {SITE.location}
                </Typography>
                <Typography variant="h1" sx={{ maxWidth: 900 }}>
                    Software Engineering, Automation <Box component="br" sx={{ display: { xs: 'none', md: 'block' } }} />
                    &amp; Technical Consulting
                </Typography>
                <Typography sx={{ mt: 3, maxWidth: 640, color: COLORS.mutedOnDark, fontSize: { xs: '1.1rem', md: '1.3rem' }, lineHeight: 1.55 }}>
                    From small business websites and one-off automations to custom applications and technical consulting,
                    I help people solve real problems with software.
                </Typography>
                <Box sx={{ mt: 5, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    <TvButton text="Start a Project" to="/contact" size="large" icon={<ArrowForwardIcon />} />
                    <TvButton text="See How I Can Help" to="/services" size="large" variant="outline" tone="dark" />
                </Box>
                <Typography sx={{ mt: 2.5, color: COLORS.mutedOnDark, fontSize: '0.95rem' }}>
                    Small, well-defined projects are welcome. A single script or a simple website is a great place to start.
                </Typography>

                <Box sx={{ mt: { xs: 7, md: 10 }, pt: 4, borderTop: `1px solid ${COLORS.blackBorder}`, display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
                    {credentials.map((c) => (
                        <Box key={c.label}>
                            <Typography sx={{ fontWeight: 700, fontSize: { xs: '1rem', md: '1.1rem' } }}>{c.label}</Typography>
                            <Typography sx={{ color: COLORS.mutedOnDark, fontSize: '0.9rem', mt: 0.5 }}>{c.detail}</Typography>
                        </Box>
                    ))}
                </Box>
            </Container>
        </Box>
    )
}

function Home() {
    usePageMeta({
        title: 'Thomas Viles | Freelance Software Engineer & AWS Consultant in Indianapolis',
        description: 'Freelance software engineer in Indianapolis. Small business websites, custom software development, business automation, AWS and architecture consulting, technical rescue and developer mentorship.',
        path: '/',
    })

    return (
        <>
            <Hero />

            {/* Problems */}
            <Section tone="light">
                <SectionTitle
                    eyebrow="Sound familiar?"
                    text="Tell me what’s annoying, expensive, repetitive, or broken."
                    intro="I’ll help figure out whether software can fix it. Most of my work starts with one of these."
                />
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' } }}>
                    {painPoints.map((p) => (
                        <Link
                            key={p.text}
                            component={RouterLink}
                            to={p.serviceId === 'mentorship' ? '/mentorship' : `/services#${p.serviceId}`}
                            underline="none"
                            sx={{
                                display: 'flex', gap: 1.5, p: 2.5, borderRadius: 2, backgroundColor: COLORS.white,
                                border: `1px solid ${COLORS.border}`, color: COLORS.black,
                                transition: 'border-color 120ms', '&:hover': { borderColor: COLORS.accent },
                            }}
                        >
                            <CheckIcon sx={{ color: COLORS.accentOnLight, mt: 0.3, flexShrink: 0 }} fontSize="small" />
                            <Typography sx={{ fontSize: '1rem' }}>{p.text}</Typography>
                        </Link>
                    ))}
                </Box>
            </Section>

            {/* Services */}
            <Section id="services">
                <SectionTitle
                    eyebrow="Services"
                    text="How I can help"
                    intro="Six ways to work together, from a simple website or a focused review to building something new from scratch."
                />
                <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' } }}>
                    {services.map((s) => <ServiceCard key={s.id} service={s} />)}
                    {/* Full-width band so six service cards plus this one don't leave an orphan in the grid */}
                    <Box sx={{ gridColumn: '1 / -1', p: 3.5, borderRadius: 3, backgroundColor: COLORS.black, color: COLORS.white, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, justifyContent: 'space-between', gap: 3 }}>
                        <Box>
                            <Typography variant="h3" component="p">{SITE.manualTagline}</Typography>
                            <Typography variant="h3" component="p" sx={{ color: COLORS.accent }}>{SITE.manualPunchline}</Typography>
                        </Box>
                        <Box><TvButton text="Start a Project" to="/contact" icon={<ArrowForwardIcon />} /></Box>
                    </Box>
                </Box>

                {/* A real small business site, so website clients see an example without leaving the homepage */}
                {clientSite && (
                    <Box sx={{ mt: 2.5 }}>
                        <ProjectCard
                            project={clientSite}
                            layout="horizontal"
                            compact
                            eyebrow="Recent client website"
                            actions={<TvButton text="Get a website like this" to={`/contact?topic=${encodeURIComponent('Small business website')}`} size="small" icon={<ArrowForwardIcon fontSize="small" />} />}
                        />
                    </Box>
                )}
            </Section>

            {/* Case studies */}
            <Section tone="light">
                <SectionTitle eyebrow="Selected work" text="Real problems, practical fixes" intro="A few examples from my full-time engineering roles. Details are kept general out of respect for employer confidentiality." />
                <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                    {caseStudies.filter((c) => c.featured).slice(0, 4).map((c) => <CaseStudyCard key={c.id} study={c} compact />)}
                </Box>
                <Box sx={{ mt: 4 }}>
                    <TvButton text="See all work" to="/work" variant="outline" icon={<ArrowForwardIcon />} />
                </Box>
            </Section>

            {/* About / trust */}
            <Section>
                <Box sx={{ display: 'grid', gap: { xs: 5, md: 8 }, gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, alignItems: 'center' }}>
                    <Box
                        component="img"
                        src={require('../../media/ProfilePic.jpg')}
                        alt="Thomas Viles"
                        sx={{ width: '100%', maxWidth: { xs: 260, md: 320 }, aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: 4, border: `4px solid ${COLORS.black}`, boxShadow: `10px 10px 0 ${COLORS.accent}` }}
                    />
                    <Box>
                        <SectionTitle eyebrow="About" text="Hi, I’m Thomas." />
                        <Box sx={{ mt: -2, display: 'grid', gap: 2, color: 'text.secondary', maxWidth: 680 }}>
                            <Typography>
                                I’m a full-stack software engineer who has been building production software professionally since 2018,
                                most recently with React, TypeScript, Python and AWS serverless architecture. I’ve built customer-facing
                                applications, internal admin tools and APIs, and I’ve been the person teams go to with AWS questions.
                            </Typography>
                            <Typography>
                                I’m a U.S. Army veteran and came into software through Flatiron School. I care about understanding the business
                                problem first, then building the simplest thing that solves it well.
                            </Typography>
                        </Box>
                        <Typography variant="overline" component="p" sx={{ mt: 4, color: COLORS.muted }}>Experience includes</Typography>
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, sm: 3 } }}>
                            {employers.map((e) => (
                                <Typography key={e} sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{e}</Typography>
                            ))}
                        </Box>
                        <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            <TvButton text="View Resume" to="/resume" variant="outline" />
                            <TvButton text="LinkedIn Recommendations" link={SITE.linkedinRecommendations} variant="outline" />
                        </Box>
                    </Box>
                </Box>
            </Section>

            {/* Process */}
            <Section tone="light">
                <SectionTitle eyebrow="How it works" text="Starting is easy" />
                <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' } }}>
                    {processSteps.map((step, i) => (
                        <Box component="li" key={step.title} sx={{ p: 3, borderRadius: 3, backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}` }}>
                            <Typography sx={{ fontWeight: 800, color: COLORS.accentOnLight, fontSize: '0.9rem' }}>0{i + 1}</Typography>
                            <Typography variant="h4" component="h3" sx={{ mt: 1 }}>{step.title}</Typography>
                            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>{step.body}</Typography>
                        </Box>
                    ))}
                </Box>
            </Section>

            {/* Tech */}
            <Section tight>
                <SectionTitle eyebrow="Tools I use" text="Built on proven technology" intro="I pick tools based on what fits the problem, but these are the ones I know best." />
                <TechStack />
            </Section>

            <CtaBanner />
        </>
    );
}

export default Home;
